/**
 * Store-relative clock helpers.
 *
 * Anything that decides "has this day happened yet" has to ask the store, not
 * the browser. A manager in New York looking at a Los Angeles store, or any
 * store whose business day starts after midnight, will otherwise roll over to
 * the next day early and count shifts that have not been worked.
 */

/**
 * Return a Date object representing "now" in the given IANA timezone.
 *
 * Live Day Labor reads this as a Date, so it keeps that shape. Prefer
 * getStoreClockParts for new callers: a local Date cannot hold a wall-clock
 * time that does not exist in the viewer's zone during a DST transition.
 * The fields are the store's wall clock, so getHours()/getDate() read as the
 * store sees them. Falls back to the browser's local clock when the timezone
 * is unavailable.
 */
export function getStoreNow(storeTimezone) {
  if (!storeTimezone) return new Date();
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: storeTimezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value;
    return new Date(
      Number(get("year")),
      Number(get("month")) - 1,
      Number(get("day")),
      Number(get("hour")),
      Number(get("minute")),
      Number(get("second"))
    );
  } catch {
    return new Date();
  }
}

/** Format a Date's local fields as YYYY-MM-DD, matching the schedule APIs. */
export function formatLocalDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * The store's wall clock right now as plain numbers.
 *
 * Deliberately not a Date. A Date built from local fields cannot represent a
 * time that does not exist in the viewer's zone: on the viewer's spring-forward
 * date, a store still at 2:30 would be silently normalized to 3:30, which is
 * enough to move a business day that starts at 3. Falls back to the browser's
 * own clock when the timezone is missing or unrecognized.
 */
export function getStoreClockParts(storeTimezone) {
  if (storeTimezone) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: storeTimezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).formatToParts(new Date());
      const get = (type) => Number(parts.find((p) => p.type === type)?.value);
      const hour = get("hour");
      return {
        year: get("year"),
        month: get("month"),
        day: get("day"),
        // Some locales render midnight as hour 24.
        hour: hour === 24 ? 0 : hour,
        minute: get("minute"),
      };
    } catch {
      // Fall through to the browser clock.
    }
  }
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
    hour: now.getHours(),
    minute: now.getMinutes(),
  };
}

/**
 * The store's current business date as YYYY-MM-DD. Before the business day
 * starts we are still in the previous one, which is how the backend stamps
 * `business_date` on timecards and `date` on shifts.
 */
export function getStoreBusinessDate(storeTimezone, startQhnum) {
  const { year, month, day, hour, minute } = getStoreClockParts(storeTimezone);
  const start = Number(startQhnum);
  // Stepping back a day in UTC, which has no DST, so the arithmetic cannot
  // land on an hour that does not exist.
  const date = new Date(Date.UTC(year, month - 1, day));
  if (Number.isFinite(start) && hour + minute / 60 < start) {
    date.setUTCDate(date.getUTCDate() - 1);
  }
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${date.getUTCFullYear()}-${mm}-${dd}`;
}

/**
 * The store's current time as hours on the schedule's own axis.
 *
 * A store whose business day starts after midnight keeps counting past 24 on
 * that day's axis, so 1:30am on a 4am-start store reads as 25.5 and compares
 * correctly against a shift that ends at 25.0 rather than looking like the
 * earliest moment of the day.
 */
export function getStoreHoursNow(storeTimezone, startQhnum) {
  const { hour, minute } = getStoreClockParts(storeTimezone);
  const hours = hour + minute / 60;
  // start_qhnum is an hour, not a count of quarter hours: the model documents
  // 30 as 6am the next day. getStoreBusinessDate above compares it the same
  // way, and the two have to agree on where the day turns over.
  const start = Number(startQhnum);
  const dayStart = Number.isFinite(start) ? start : 0;
  return hours < dayStart ? hours + 24 : hours;
}
